import { createFileRoute, useNavigate, useRouter } from "@tanstack/react-router";
import React, { Suspense, useEffect } from "react";
import { useTranslation } from "react-i18next";
import SINGLE_GROUP_CONTEXT from "src/context/internal/group";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "src/components/base/table";
import { Subheading } from "src/components/base/heading";
import { TrashIcon } from "@heroicons/react/20/solid";
import { Button, PrimaryButton } from "src/components/base/button";
import { ApiInternal } from "src/api/api_internal";
import { useForm } from "@tanstack/react-form";
import TablePagination from "src/components/table-pagination";
import Form from "src/components/base/form";
import { Field, FieldGroup, Fieldset, Label } from "src/components/base/fieldset";
import { Input } from "src/components/base/input";
import WS from "src/api/ws";
import GroupAddMemberDialog from "src/components/dialogs/group-add-member";

/**
 * The properties for {@link GeneralGroupView}
 */
export type GeneralGroupViewProps = {};

const LIMIT = 25;

/**
 * The overview for a group
 */
function GeneralGroupView(props: GeneralGroupViewProps) {
    const [t] = useTranslation("admin-group-view");
    const [tg] = useTranslation();

    const ctx = React.useContext(SINGLE_GROUP_CONTEXT);

    const navigate = useNavigate();
    const router = useRouter();

    const { search, page } = Route.useSearch();
    const data = Route.useLoaderData();

    const [openAddMember, setOpenAddMember] = React.useState(false);

    const form = useForm({
        defaultValues: {
            search: search,
        },
    });

    useEffect(() => {
        const listener = WS.addEventListener("message.MembersChanged", (event) => {
            if (event.group_uuid === ctx.group.uuid) {
                router.invalidate({ sync: true });
            }
        });

        return () => WS.removeEventListener(listener);
    }, [ctx.group.uuid]);

    return (
        <>
            <Subheading>{t("heading.member")}</Subheading>
            <div className={"flex flex-col gap-6"}>
                <Form className={"mt-6 grid grid-cols-2 gap-12"} onSubmit={form.handleSubmit}>
                    <Fieldset>
                        <FieldGroup>
                            <form.Field
                                name={"search"}
                                validators={{
                                    onChangeAsync: async ({ fieldApi }) =>
                                        await navigate({
                                            to: "/a/groups/$groupId/general",
                                            params: { groupId: ctx.group.uuid },
                                            search: {
                                                page: 1,
                                                search: fieldApi.state.value,
                                            },
                                        }),
                                    onChangeAsyncDebounceMs: 500,
                                }}
                            >
                                {(fieldApi) => (
                                    <Field>
                                        <Label>{t("label.filter")}</Label>
                                        <Input
                                            value={fieldApi.state.value}
                                            onChange={(e) => fieldApi.handleChange(e.target.value)}
                                        />
                                    </Field>
                                )}
                            </form.Field>
                        </FieldGroup>
                    </Fieldset>

                    {ctx.group.provider === "Local" && (
                        <div className={"flex justify-end self-end"}>
                            <PrimaryButton onClick={() => setOpenAddMember(true)}>
                                {t("button.add-to-group")}
                            </PrimaryButton>
                        </div>
                    )}
                </Form>
                <Table dense={true} className={"mt-3"}>
                    <TableHead>
                        <TableRow>
                            <TableHeader>{t("label.name")}</TableHeader>
                            <TableHeader>{t("label.external-id")}</TableHeader>
                            {ctx.group.provider === "Local" && (
                                <TableHeader>
                                    <span className={"sr-only"}>{tg("label.external-id")}</span>
                                </TableHeader>
                            )}
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {data.items.map((member) => (
                            <TableRow key={member.uuid}>
                                <TableCell>{member.display_name}</TableCell>
                                <TableCell>{member.external_id}</TableCell>
                                {ctx.group.provider === "Local" && (
                                    <TableCell>
                                        <Button
                                            plain={true}
                                            onClick={async () => {
                                                await ApiInternal.groups.local.removeMember(
                                                    ctx.group.uuid,
                                                    member.uuid,
                                                );
                                                router.invalidate({ sync: true });
                                            }}
                                        >
                                            <TrashIcon />
                                        </Button>
                                    </TableCell>
                                )}
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
                <TablePagination
                    currentPage={page}
                    maxPages={data.total == 0 ? 1 : Math.ceil(data.total / LIMIT)}
                    href={"/a/groups/$groupId/general"}
                    params={{ groupId: ctx.group.uuid }}
                    getSearchParams={(newPage: number) => ({ search, page: newPage })}
                />
            </div>

            {openAddMember && (
                <Suspense>
                    <GroupAddMemberDialog
                        onClose={() => setOpenAddMember(false)}
                        group={ctx.group}
                        onMemberAdd={async () => {
                            setOpenAddMember(false);
                            await router.invalidate({ sync: true });
                        }}
                    />
                </Suspense>
            )}
        </>
    );
}

/**
 * Type that defines the search parameter of this endpoint
 */
type MembersOverviewSearch = {
    /** The search term that should be used */
    search: string;
    /** The current page number we're on */
    page: number;
};

export const Route = createFileRoute("/_admin/a/groups/$groupId/_group/general")({
    component: GeneralGroupView,

    validateSearch: (search: Record<string, unknown>): MembersOverviewSearch => {
        const page = Number(search?.page ?? 1);
        return { page: page <= 0 ? 1 : page, search: (search.search as string) || "" };
    },
    loaderDeps: ({ search: { search, page } }) => ({ search, page }),
    loader: async ({ deps, params }) =>
        await ApiInternal.groups.getMembers(params.groupId, {
            limit: LIMIT,
            offset: (deps.page - 1) * LIMIT,
            filter_name: deps.search,
        }),
});
